import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Auth.css';

const AGE_RANGES = ['Under 18', '18–24', '25–34', '35–44', '45–54', '55+'];
const CONTENT_PREFS = [
  'Mindset',
  'Productivity',
  'Finance & Money',
  'Relationships',
  'Health & Wellness',
  'Leadership',
  'Spiritual & Philosophy',
  'Creativity',
];
const PAYMENT_PREFS = ['One-time', 'Monthly', 'Annual', 'Lifetime', 'Not sure yet'];

export default function SignupPage() {
  const { signup } = useAuth();
  // Required fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Optional preference fields (migrated from old WaitlistMultiStep)
  const [phone, setPhone] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [contentPrefs, setContentPrefs] = useState([]);
  const [paymentPref, setPaymentPref] = useState('');
  const [consent, setConsent] = useState(true);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleContent = (c) =>
    setContentPrefs((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !password) {
      setError('Please fill in name, email and password.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        name: name.trim(),
        email: email.trim(),
        password,
        phone: phone.trim() || undefined,
        age_range: ageRange || undefined,
        content_prefs: contentPrefs.length ? contentPrefs : undefined,
        payment_pref: paymentPref || undefined,
        consent: !!consent,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="auth-page" data-testid="signup-page">
        <Link to="/" className="auth-back">&larr; Back to iCraftz</Link>
        <div className="auth-card">
          <img src="/seal/icraftz-wax-seal-small.png" alt="" className="auth-card-seal" aria-hidden="true" />
          <h1 className="auth-title">Check your <span className="auth-title-accent">Inbox</span></h1>
          <p className="auth-sub" data-testid="signup-neutral-message">If this email can be used, we&apos;ve sent a verification link to <strong>{email}</strong>.</p>
          <div className="auth-divider"><span className="auth-divider-line" /><span className="auth-divider-mark">❦</span><span className="auth-divider-line" /></div>
          <p className="auth-message auth-message-info">
            Click the link in the email to verify your account. Once verified, you can log in.
          </p>
          <p className="auth-footer">
            Already verified? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" data-testid="signup-page">
      <Link to="/" className="auth-back">&larr; Back to iCraftz</Link>
      <div className="auth-card auth-card-wide">
        <img src="/seal/icraftz-wax-seal-small.png" alt="" className="auth-card-seal" aria-hidden="true" />
        <h1 className="auth-title">Begin your <span className="auth-title-accent">Journey</span></h1>
        <p className="auth-sub">A private sanctuary for every chapter that moves you.</p>
        <div className="auth-divider"><span className="auth-divider-line" /><span className="auth-divider-mark">❦</span><span className="auth-divider-line" /></div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {/* — Required — */}
          <div className="auth-section">
            <p className="auth-section-eyebrow">YOUR ACCOUNT</p>
            <div className="auth-row">
              <div className="auth-col">
                <p className="auth-field-label">Your name</p>
                <input
                  type="text" className="auth-input" placeholder="Your name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name" autoComplete="name" required maxLength={120}
                />
              </div>
              <div className="auth-col">
                <p className="auth-field-label">Phone <span className="auth-optional">(optional)</span></p>
                <input
                  type="tel" className="auth-input" placeholder="+91 98XXXXXXXX"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  data-testid="signup-phone" autoComplete="tel" maxLength={32}
                />
              </div>
            </div>
            <div>
              <p className="auth-field-label">Email</p>
              <input
                type="email" className="auth-input" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email" autoComplete="email" required
              />
            </div>
            <div>
              <p className="auth-field-label">Password (min 8 characters)</p>
              <input
                type="password" className="auth-input" placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password" autoComplete="new-password" required minLength={8}
              />
            </div>
          </div>

          {/* — Optional preferences — */}
          <div className="auth-section">
            <p className="auth-section-eyebrow">A FEW DETAILS <span className="auth-optional">(optional — helps us personalise your path)</span></p>

            <div>
              <p className="auth-field-label">Age range</p>
              <div className="auth-pill-row" data-testid="signup-age">
                {AGE_RANGES.map((a) => (
                  <label key={a} className={`auth-pill ${ageRange === a ? 'is-on' : ''}`}>
                    <input type="radio" name="age" value={a}
                           checked={ageRange === a}
                           onChange={(e) => setAgeRange(e.target.value)} />
                    {a}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="auth-field-label">What you want to grow in <span className="auth-optional">(choose up to 3)</span></p>
              <div className="auth-pill-row" data-testid="signup-content">
                {CONTENT_PREFS.map((c) => {
                  const on = contentPrefs.includes(c);
                  const disabled = !on && contentPrefs.length >= 3;
                  return (
                    <label
                      key={c}
                      className={`auth-pill ${on ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''}`}
                    >
                      <input type="checkbox" checked={on} disabled={disabled}
                             onChange={() => !disabled && toggleContent(c)} />
                      {c}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="auth-field-label">If iCraftz becomes your daily practice, you&apos;d prefer to pay</p>
              <div className="auth-pill-row" data-testid="signup-payment">
                {PAYMENT_PREFS.map((p) => (
                  <label key={p} className={`auth-pill ${paymentPref === p ? 'is-on' : ''}`}>
                    <input type="radio" name="pay" value={p}
                           checked={paymentPref === p}
                           onChange={(e) => setPaymentPref(e.target.value)} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <label className="auth-consent" data-testid="signup-consent">
            <input
              type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            />
            <span>I&apos;d like occasional updates from iCraftz — you can unsubscribe any time.</span>
          </label>

          <button type="submit" className="auth-submit" disabled={submitting} data-testid="signup-submit">
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>

          {error && <p className="auth-message auth-message-error" data-testid="signup-error">{error}</p>}
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
